import { Info } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { client } from '@/api/client'
import { useAuthStore } from '@/stores/auth'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { UppercaseInput } from '@/components/ui/uppercase-input'
import { Label } from '@/components/ui/label'

interface AddChildFormData {
  first_name: string
  last_name: string
  social_security_number: string
  gender: string
  birth_date: string
  family_status: string
  parent_type: string
  co_parent_type?: string
  guardians: string[]
  social_security_fund_owner?: number
}


export function AddChildPage() {
  const { t } = useTranslation('children')
  const navigate = useNavigate()
  const { parent } = useAuthStore()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasCoParent, setHasCoParent] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError: setFieldError,
    watch,
    setValue,
    clearErrors,
    getValues
  } = useForm<AddChildFormData>({
    defaultValues: {
      guardians: []
    }
  })

  // Watch the parent_type to determine complementary co-parent type
  const parentType = watch('parent_type')
  const coParentType = watch('co_parent_type')
  const guardians = watch('guardians')

  // Use a ref to track the previous parent type values to prevent infinite loops
  const prevParentTypeRef = useRef(parentType);
  const prevCoParentTypeRef = useRef(coParentType);

  // Check if the parent has a co-parent
  useEffect(() => {
    if (parent?.co_parent) {
      setHasCoParent(true)
    }
    
    // Initialize guardians with the primary parent by default
    if (parent?.id) {
      const initialGuardians = ['parent'];
      setValue('guardians', initialGuardians);
    }
  }, [parent, setValue])

  // Validate that parent_type and co_parent_type are different
  useEffect(() => {
    // Only run this logic if both values have changed from their previous values
    // This prevents unnecessary validation cycles
    const parentTypeChanged = parentType !== prevParentTypeRef.current;
    const coParentTypeChanged = coParentType !== prevCoParentTypeRef.current;
    
    if ((parentTypeChanged || coParentTypeChanged) && hasCoParent) {
      // Update refs with current values
      prevParentTypeRef.current = parentType;
      prevCoParentTypeRef.current = coParentType;
      
      // Check for duplicate parent types
      if (parentType && coParentType && parentType === coParentType) {
        setFieldError('co_parent_type', {
          type: 'manual',
          message: t('form.validation.duplicateParentType')
        });
      } else if (coParentType && parentType) {
        // Only clear the error if both values exist and are different
        clearErrors('co_parent_type');
      }
    }
  }, [parentType, coParentType, hasCoParent, setFieldError, clearErrors, t]);

  // Handle guardian selection changes
  const handleGuardianChange = (value: string) => {
    // Get current guardians from the form
    const currentGuardians = guardians || [];
    let newGuardians: string[] = [...currentGuardians];
    
    if (newGuardians.includes(value)) {
      // If already selected, remove it
      newGuardians = newGuardians.filter(g => g !== value);
    } else {
      // Otherwise add it
      newGuardians.push(value);
    }
    
    // Update the form value directly
    setValue('guardians', newGuardians);
  };

  const onSubmit = async (data: AddChildFormData) => {
    setIsLoading(true)
    setError(null)

    // Validate that parent_type and co_parent_type are different
    if (hasCoParent && data.parent_type && data.co_parent_type && data.parent_type === data.co_parent_type) {
      setFieldError('co_parent_type', {
        type: 'manual',
        message: t('form.validation.duplicateParentType')
      });
      setIsLoading(false);
      return;
    }

    // Validate that co_parent_type is provided if the user has a co-parent
    if (hasCoParent && parent?.co_parent && (!data.co_parent_type || data.co_parent_type === '')) {
      setFieldError('co_parent_type', {
        type: 'manual',
        message: t('form.validation.coParentTypeRequired')
      });
      setIsLoading(false);
      return;
    }

    // Validate that at least one guardian is selected
    if (!data.guardians || data.guardians.length === 0) {
      setFieldError('guardians', {
        type: 'manual',
        message: t('form.validation.guardiansRequired')
      });
      setIsLoading(false);
      return;
    }

    try {
      // Extract parent_type from the data
      const { parent_type, co_parent_type, guardians, ...childData } = data;
      
      // Convert guardian strings to parent IDs
      const guardianIds: number[] = [];
      if (guardians.includes('parent') && parent?.id) {
        guardianIds.push(parent.id);
      }
      if (guardians.includes('coParent') && parent?.co_parent?.id) {
        guardianIds.push(parent.co_parent.id);
      }
      
      // Create the request data with the correct structure
      const requestData: any = {
        ...childData,
        parent_type,
        guardians: guardianIds,
        social_security_fund_owner: childData.social_security_fund_owner || parent?.id
      };
      
      // Only include co_parent_type if it's provided and we have a co-parent
      if (hasCoParent && co_parent_type && parent?.co_parent) {
        requestData.co_parent_type = co_parent_type;
      }

      // Create the child
      await client.post('/children/', requestData);
      
      navigate('/');
    } catch (error: any) {
      // Error handling without logging to console
      
      if (error.response?.data) {
        // Handle field-specific errors
        Object.entries(error.response.data).forEach(([field, messages]) => {
          if (field as keyof AddChildFormData) {
            // Check if this is the AMKA date format error message
            let errorMessage = Array.isArray(messages) ? messages[0] : messages;
            
            // Translate specific backend error messages
            if (typeof errorMessage === 'string') {
              if (errorMessage.includes('The first 6 digits of AMKA must represent a valid date in DDMMYY format')) {
                errorMessage = t('form.validation.ssnDateFormat');
              } else if (errorMessage.includes('The first 6 digits of AMKA must match the birth date')) {
                errorMessage = t('form.validation.ssnBirthDateMismatch');
              } else if (errorMessage.includes('Child with this Social Security Number already exists')) {
                errorMessage = t('form.validation.ssnExists');
              } else if (errorMessage.includes('Social security fund owner must have a social security number')) {
                // Don't set the global error message since we're handling it with a custom element
                
                // Create a custom error handler that will show the message with a link
                const targetParentId = getValues('social_security_fund_owner');
                const handleSSNLinkClick = () => {
                  sessionStorage.setItem('scrollToSSN', 'true');
                  sessionStorage.setItem('highlightSSN', 'true');
                  sessionStorage.setItem('targetParentId', String(targetParentId));
                };
                
                // Override the error display in the form for this field
                const socialSecurityField = document.querySelector('[id="social_security_fund_owner"]')?.parentElement;
                if (socialSecurityField) {
                  const errorP = socialSecurityField.querySelector('p.text-destructive') || document.createElement('p');
                  errorP.className = 'text-destructive text-xs mt-1';
                  const linkText = t('form.validation.ssnRequiredForFundOwner');
                  const [beforeHere, afterHere] = linkText.split('εδώ');
                  const [beforeHereEn, afterHereEn] = linkText.split('here');
                  
                  // Use the appropriate split based on the current language
                  const isGreek = beforeHere !== linkText;
                  if (isGreek) {
                    errorP.innerHTML = `${beforeHere}<a href="/profile/edit" class="text-blue-600 underline hover:text-blue-800" id="ssn-link">εδώ</a>${afterHere || ''}`;
                  } else {
                    errorP.innerHTML = `${beforeHereEn}<a href="/profile/edit" class="text-blue-600 underline hover:text-blue-800" id="ssn-link">here</a>${afterHereEn || ''}`;
                  }
                  
                  if (!socialSecurityField.querySelector('p.text-destructive')) {
                    socialSecurityField.appendChild(errorP);
                  }
                  
                  document.getElementById('ssn-link')?.addEventListener('click', handleSSNLinkClick);
                }
                
                // Still set the error but with an empty message
                setFieldError('social_security_fund_owner', {
                  type: 'manual',
                  message: ''
                });
                
                // Clear the errorMessage to prevent it from showing in global error section
                errorMessage = '';
              } else if (errorMessage.includes('This field may not be blank') && field === 'co_parent_type') {
                errorMessage = t('form.validation.coParentTypeRequired');
              }
            }
            
            setFieldError(field as keyof AddChildFormData, {
              type: 'manual',
              message: errorMessage,
            })
          } else {
            let errorMessage = Array.isArray(messages) ? messages[0] : messages;
            
            // Translate specific backend error messages
            if (typeof errorMessage === 'string') {
              if (errorMessage.includes('The first 6 digits of AMKA must represent a valid date in DDMMYY format')) {
                errorMessage = t('form.validation.ssnDateFormat');
              } else if (errorMessage.includes('The first 6 digits of AMKA must match the birth date')) {
                errorMessage = t('form.validation.ssnBirthDateMismatch');
              } else if (errorMessage.includes('Child with this Social Security Number already exists')) {
                errorMessage = t('form.validation.ssnExists');
              } else if (errorMessage.includes('Social security fund owner must have a social security number')) {
                errorMessage = t('form.validation.ssnRequiredForFundOwner');
                // Create a custom error handler that will show the message with a link
                const targetParentId = getValues('social_security_fund_owner');
                const handleSSNLinkClick = () => {
                  sessionStorage.setItem('scrollToSSN', 'true');
                  sessionStorage.setItem('highlightSSN', 'true');
                  sessionStorage.setItem('targetParentId', String(targetParentId));
                };
                
                // Override the error display in the form for this field
                const socialSecurityField = document.querySelector('[id="social_security_fund_owner"]')?.parentElement;
                if (socialSecurityField) {
                  const errorP = socialSecurityField.querySelector('p.text-destructive') || document.createElement('p');
                  errorP.className = 'text-destructive text-xs mt-1';
                  const linkText = t('form.validation.ssnRequiredForFundOwner');
                  errorP.innerHTML = `${linkText.split('here')[0]} <a href="/profile/edit" class="text-blue-600 underline hover:text-blue-800" id="ssn-link">here</a>`;
                  
                  if (!socialSecurityField.querySelector('p.text-destructive')) {
                    socialSecurityField.appendChild(errorP);
                  }
                  
                  document.getElementById('ssn-link')?.addEventListener('click', handleSSNLinkClick);
                }
                
                // Still set the error but with a basic message
                setFieldError('social_security_fund_owner', {
                  type: 'manual',
                  message: ''
                });
              }
            }
            
            setError(errorMessage)
          }
        })
      } else {
        setError(t('errors.addFailed'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  const selectClass = "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>{t('add.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* First Name and Last Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">{t('form.firstName')}</Label>
                <UppercaseInput id="first_name" {...register('first_name', { required: t('form.validation.firstNameRequired') })} />
                {errors.first_name && <p className="text-xs text-destructive">{errors.first_name.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">{t('form.lastName')}</Label>
                <UppercaseInput id="last_name" {...register('last_name', { required: t('form.validation.lastNameRequired') })} />
                {errors.last_name && <p className="text-xs text-destructive">{errors.last_name.message}</p>}
              </div>
            </div>

            <div className="flex items-start gap-2 p-2 bg-muted rounded-md text-xs text-muted-foreground">
              <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>{t('form.nameRequirements')}</span>
            </div>

            {/* SSN and Birth Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="social_security_number">
                  {t('form.ssn')} <span className="text-xs font-normal text-muted-foreground">{t('form.childSuffix')}</span>
                </Label>
                <Input
                  id="social_security_number"
                  {...register('social_security_number', {
                    required: t('form.validation.ssnRequired'),
                    pattern: { value: /^\d{11}$/, message: t('form.validation.ssnFormat') }
                  })}
                />
                {errors.social_security_number && <p className="text-xs text-destructive">{errors.social_security_number.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="birth_date">{t('form.birthDate')}</Label>
                <Input id="birth_date" type="date" {...register('birth_date', { required: t('form.validation.birthDateRequired') })} />
                {errors.birth_date && <p className="text-xs text-destructive">{errors.birth_date.message}</p>}
              </div>
            </div>

            {/* Gender and Family Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="gender">{t('form.gender')}</Label>
                <select id="gender" {...register('gender', { required: t('form.validation.genderRequired') })} className={selectClass}>
                  <option value="">{t('form.gender')}</option>
                  <option value="male">{t('form.genderOptions.male')}</option>
                  <option value="female">{t('form.genderOptions.female')}</option>
                </select>
                {errors.gender && <p className="text-xs text-destructive">{errors.gender.message}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="family_status">{t('form.familyStatus')}</Label>
                <select id="family_status" {...register('family_status', { required: t('form.validation.familyStatusRequired') })} className={selectClass}>
                  <option value="">{t('form.familyStatus')}</option>
                  <option value="married">{t('form.familyStatusOptions.married')}</option>
                  <option value="divorced">{t('form.familyStatusOptions.divorced')}</option>
                  <option value="separated">{t('form.familyStatusOptions.separated')}</option>
                  <option value="widowed_parent">{t('form.familyStatusOptions.widowed_parent')}</option>
                  <option value="single_parent">{t('form.familyStatusOptions.single_parent')}</option>
                  <option value="without_parents">{t('form.familyStatusOptions.without_parents')}</option>
                </select>
                {errors.family_status && <p className="text-xs text-destructive">{errors.family_status.message}</p>}
              </div>
            </div>

            {/* Parent Type */}
            {hasCoParent ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="parent_type">{t('form.parentType')}</Label>
                  <select id="parent_type" {...register('parent_type', { required: t('form.validation.parentTypeRequired') })} className={selectClass}>
                    <option value="">{t('form.parentType')}</option>
                    <option value="father">{t('form.parentTypeOptions.father')}</option>
                    <option value="mother">{t('form.parentTypeOptions.mother')}</option>
                    <option value="parent_one">{t('form.parentTypeOptions.parent_one')}</option>
                    <option value="parent_two">{t('form.parentTypeOptions.parent_two')}</option>
                    <option value="other">{t('form.parentTypeOptions.other')}</option>
                  </select>
                  {errors.parent_type && <p className="text-xs text-destructive">{errors.parent_type.message}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="co_parent_type">{t('form.coParentType')}</Label>
                  <select id="co_parent_type" {...register('co_parent_type', { required: t('form.validation.coParentTypeRequired') })} className={selectClass}>
                    <option value="">{t('form.coParentTypeNone')}</option>
                    <option value="father">{t('form.parentTypeOptions.father')}</option>
                    <option value="mother">{t('form.parentTypeOptions.mother')}</option>
                    <option value="parent_one">{t('form.parentTypeOptions.parent_one')}</option>
                    <option value="parent_two">{t('form.parentTypeOptions.parent_two')}</option>
                    <option value="other">{t('form.parentTypeOptions.other')}</option>
                  </select>
                  {errors.co_parent_type && <p className="text-xs text-destructive">{errors.co_parent_type.message}</p>}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="parent_type">{t('form.parentType')}</Label>
                <select id="parent_type" {...register('parent_type', { required: t('form.validation.parentTypeRequired') })} className={selectClass}>
                  <option value="">{t('form.parentType')}</option>
                  <option value="father">{t('form.parentTypeOptions.father')}</option>
                  <option value="mother">{t('form.parentTypeOptions.mother')}</option>
                  <option value="parent_one">{t('form.parentTypeOptions.parent_one')}</option>
                  <option value="parent_two">{t('form.parentTypeOptions.parent_two')}</option>
                  <option value="other">{t('form.parentTypeOptions.other')}</option>
                </select>
                {errors.parent_type && <p className="text-xs text-destructive">{errors.parent_type.message}</p>}
              </div>
            )}

            {/* Guardians */}
            <div className="space-y-2">
              <Label>{t('form.guardians')}</Label>
              <p className="text-xs text-muted-foreground">{t('form.guardiansDescription')}</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input type="checkbox" id="guardian_parent" checked={guardians?.includes('parent')} onChange={() => handleGuardianChange('parent')} className="h-4 w-4 rounded border-input" />
                  <label htmlFor="guardian_parent" className="text-sm cursor-pointer">{parent?.first_name} {parent?.last_name}</label>
                </div>
                {hasCoParent && parent?.co_parent && (
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="guardian_coparent" checked={guardians?.includes('coParent')} onChange={() => handleGuardianChange('coParent')} className="h-4 w-4 rounded border-input" />
                    <label htmlFor="guardian_coparent" className="text-sm cursor-pointer">{parent.co_parent.first_name} {parent.co_parent.last_name}</label>
                  </div>
                )}
              </div>
              {errors.guardians && <p className="text-xs text-destructive">{errors.guardians.message}</p>}
            </div>

            {/* Social Security Fund Owner */}
            <div className="space-y-2">
              <Label htmlFor="social_security_fund_owner">{t('form.socialSecurityFundOwner')}</Label>
              <select id="social_security_fund_owner" {...register('social_security_fund_owner')} className={selectClass} defaultValue={parent?.id}>
                <option value={parent?.id}>{parent?.first_name} {parent?.last_name}</option>
                {hasCoParent && parent?.co_parent && (
                  <option value={parent.co_parent.id}>{parent.co_parent.first_name} {parent.co_parent.last_name}</option>
                )}
              </select>
              {errors.social_security_fund_owner && <p className="text-xs text-destructive">{errors.social_security_fund_owner.message}</p>}
            </div>

            {/* Error message */}
            {error && (
              <div className="rounded-md bg-destructive/10 border border-destructive/30 p-3">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" className="flex-1" asChild>
                <Link to="/children">{t('form.cancel')}</Link>
              </Button>
              <Button type="submit" className="flex-1" disabled={isLoading}>
                {isLoading ? t('form.submitting') : t('form.submit')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
} 